import api from "api";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import dayjs from "newui/utils/dayjs";
import { CustomToastV2 } from "components/Toast/CustomToastv2";
import { toast } from "react-toastify";
import { extractResumeFileId } from "./resumeHelpers";
import { RFX_STATUS } from "./constants";

export function cn(...inputs) {
	return twMerge(clsx(inputs));
}

export const filenameSpliter = (file) => {
	let filename = "";
	if (typeof file === "object" && file !== null) {
		filename = file?.file_name || file?.name;
	} else {
		filename = file;
	}
	const ext = filename?.split(".")?.pop();
	return {
		filename: filename,
		ext: ext,
	};
};

export const processFormData = (values) => {
	const formData = new FormData();

	for (var key in values) {
		let value = values[key];
		if (Array.isArray(value)) {
			const valueKey = key;

			if (value.length > 0) {
				value.forEach((item, index) => {
					let itemData =
						typeof item === "object" && !(item instanceof File) && item !== null
							? JSON.stringify(item)
							: item;

					//setting the array index is the major 🔑 here
					formData.append(`${valueKey}[${index}]`, itemData);
				});
			} else {
				formData.append(valueKey, "[]");
			}
		} else if (
			typeof value === "object" &&
			!(value instanceof File) &&
			value !== null
		) {
			let valueJson = JSON.stringify(value);
			formData.append(key, valueJson);
		} else {
			formData.append(key, value);
		}
	}
	return formData;
};

// TODO: Replace all uses with api-v1 or v2
export const getApiWithToken = () => {
	return api;
};
export const equalizeHeights = (elements) => {
	const elementHeights = Array.prototype.map.call(elements, (el) => {
		return el.clientHeight;
	});

	var maxHeight = Math.max(...elementHeights);

	Array.prototype.forEach.call(
		elements,
		(el) => (el.style.height = `${maxHeight}px`)
	);
};

export const capitalise = (text = "") => {
	if (typeof text === "string") {
		return `${text.charAt(0).toUpperCase()}${text.replace(/(_)/g, " ").slice(1)}`;
	} else {
		return "";
	}
};

export const formatNumber = (number) =>
	number.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");

export const shortenDigits = (labelValue) => {
	// Nine Zeroes for Billions
	return Math.abs(Number(labelValue)) >= 1.0e9
		? parseFloat(Math.abs(Number(labelValue)) / 1.0e9).toFixed(0) + "B"
		: // Six Zeroes for Millions
		Math.abs(Number(labelValue)) >= 1.0e6
			? parseFloat(Math.abs(Number(labelValue)) / 1.0e6).toFixed(0) + "M"
			: // Three Zeroes for Thousands
			Math.abs(Number(labelValue)) >= 1.0e3
				? parseFloat(Math.abs(Number(labelValue)) / 1.0e3).toFixed(0) + "K"
				: Math.abs(Number(labelValue));
};

export const getSlicedDocuments = (documents, category, isCompare) => {
	if (!isCompare) return documents.slice(0, 2);
	switch (category) {
		case 1:
		case 2:
			return documents.slice(0, 2);
		case 3:
			return documents.slice(0, 3);
		default:
			return documents;
	}
};

export const isValidAbnOrAcn = (number) => {
	number = number.toString().replaceAll(" ", "");
	if (number.length === 9) {
		return isValidAcn(number);
	}
	if (number.length === 11) {
		return isValidAbn(number);
	}
	return false;
};

const isValidAbn = (abn) => {
	const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
	abn = abn.toString();
	if (abn.length === 11) {
		let sum = 0;
		for (let i = 0; i < weights.length; i++) {
			let digit = abn[i] - (i ? 0 : 1);
			sum += digit * weights[i];
		}
		return sum % 89 === 0;
	}
	return false;
};

const isValidAcn = (acn) => {
	const weights = [8, 7, 6, 5, 4, 3, 2, 1, 0];
	acn = acn.toString();
	if (acn.length === 9) {
		let sum = 0;
		for (let i = 0; i < weights.length; i++) {
			sum += acn[i] * weights[i];
		}
		let check = (10 - (sum % 10)) % 10;
		return parseInt(acn[8]) === parseInt(check);
	}
	return false;
};

export const formatABN = (val) => {
	let copy = val;
	val = val.replace(/[^\dA-Z]/g, "");
	let reg = new RegExp(".{3}", "g");
	if (val.length <= 9) {
		val = val.replace(reg, function (a) {
			return a + " ";
		});
	} else if (val.length > 9 && val.length <= 11) {
		let firstTwo = val?.substr(0, 2);
		val = val.slice(2);
		val = val.replace(reg, function (a) {
			return a + " ";
		});
		val = firstTwo + " " + val;
	} else {
		val = copy;
	}

	return val.trim();
};

// mark properties of rules true if password is strong
// At least one character, capital character, one number, at least 8 digit
export const getPasswordStrength = (password, rule, isPAPLUser) => {
	if (!password) return rule;
	rule.number = password.match(/\d+/g) ? true : false;
	rule.letter = password.match(/[a-zA-Z]/g) ? true : false;
	rule.capital = password.match(/[A-Z]/g) ? true : false;
	rule.count = password.length >= (isPAPLUser ? 15 : 8) ? true : false;
	rule.specialCharacter = password.match(
		/^(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).*$/
	)
		? true
		: false;
	return rule;
};

export const profileTypeOptions = [
	{
		value: 1,
		label: "Client",
	},
	{
		value: 4,
		label: "Consultant",
	},
	{
		value: 2,
		label: "Head Contractor",
	},
	{
		value: 3,
		label: "Subcontractor",
	},
];

export const userRoles = [
	{
		value: "administrator",
		label: "Admin User",
	},
	{
		value: "user",
		label: "User",
	},
];

export const findUserRole = (value) =>
	userRoles.find((item) => item.value === value);

export const hasCompaniesListingAccess = (company) => {
	return Boolean(company?.allow_companies_listing_access);
};

export const isProcurer = (company) => {
	return company && company.grouping === "procurer";
};

export const isBidder = (company) => {
	return company && company.grouping === "bidder";
};

export const isProcurerNBidder = (company) => {
	return company && company.grouping === "bidder_procurer";
};

export const isClient = (profileTypeId) => {
	return profileTypeId && profileTypeId === 1;
};

/*
	@param old data and new data
    
	This function store sign up data in local storage to avoid loss 
	of data during page refresh in multistep form.

	First it gets previously saved data then merge it with old and new data 
	stringfy object and store back in storage
*/
export const preserveSignUpData = (data, newData) => {
	localStorage.setItem(
		"signup_data",
		JSON.stringify({
			...JSON.parse(localStorage.getItem("signup_data")),
			...data,
			...newData,
		})
	);
};

export const isEmptyObj = (obj) => {
	if (
		obj &&
		Object.keys(obj).length === 0 &&
		Object.getPrototypeOf(obj) === Object.prototype
	)
		return true;
	return false;
};

export const hasEmptyKeys = (obj) => {
	if (obj == null) {
		return true;
	}

	if (Array.isArray(obj)) {
		for (const item of obj) {
			if (!hasEmptyKeys(item)) {
				// item has a value
				return false;
			}
		}
		return true;
	}

	if (isEmptyObj(obj)) {
		return true;
	}

	for (const key of Object.keys(obj)) {
		const value = obj[key];
		if (value == null) {
			continue;
		}
		if (typeof value === "object") {
			if (!hasEmptyKeys(value)) {
				// item has a value
				return false;
			}
		} else if (value !== "" && value !== false && value !== 0) {
			// primitive values that are "populated"
			return false;
		}
	}

	return true;
};

export const clearSignupData = () => {
	localStorage.removeItem("signup_data");
	localStorage.removeItem("signup_step");
};

export const htmlToText = (html) => {
	return html.replace(/<[^>]*>/g, " ");
};

export const removeCarriage = (string) => {
	if (string) {
		return string.replace(/\r/g, "");
	} else {
		return "";
	}
};

export const downloadFileObject = async (file) => {
	const url = file.file_path;
	const a = document.createElement("a");
	a.href = url;
	a.download = file.name;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};

function implementImageSRC(url, callback) {
	const img = new Image();
	img.onload = function () {
		callback(false);
	};
	img.onerror = function () {
		callback(true);
	};
	img.src = url;
}

function checkingIsBroken(imageUrl) {
	return new Promise((resolve) => {
		implementImageSRC(imageUrl, (isBroken) => {
			resolve(isBroken);
		});
	});
}

export const isImageBroken = async (imageUrl) => {
	// ignore default image from backend
	if (imageUrl) {
		if (
			imageUrl.includes(
				"JjItXLpsDvuZCz8mrXvcgchzZL4qgMGaVamxLcQO.png" ||
				imageUrl.includes("usUqYOVKMloCsoUYaqWKHtzE3xeicxsJ9Y6UEiF4.png")
			)
		) {
			return true;
		}
		let isBroken = await checkingIsBroken(imageUrl);
		if (isBroken === true) {
			return true;
		} else {
			return false;
		}
	} else {
		return true;
	}
};

export const isValidURL = (url) => {
	return url && (url.startsWith("http://") || url.startsWith("https://"));
};

export const getFileNameFromUrl = (url) => {

	if (!url) {
		return null;
	}

	if (isValidURL(url)) {
		// const parts = url.split("/");
		// return parts[parts.length - 1];
		return decodeURI(url.split("/").pop().split("?")[0]);
	} else {
		return decodeURIComponent(url) || " - ";
	}
};
export const getCurrentURL = () => {
	var fullURL = window.location.href;
	var lastChar = fullURL.charAt(fullURL.length - 1);
	if (lastChar === "/") {
		return fullURL;
	} else {
		return fullURL + "/";
	}
};

export const timezoneChecker = (timezone) => {
	return timezone === "AEST" ? "Australia/Sydney" : "Australia/Perth";
};

export const grabFirstError = (errors) => {
	if (typeof errors === "object" && errors !== null) {
		for (let error in errors) {
			if (errors.hasOwnProperty(error)) {
				if (errors[error][0] !== "") {
					return errors[error][0];
				}
			}
		}
	}
	return "";
};

export const isRFQEditable = (status) => {
	return (
		typeof status === "string" &&
		[RFX_STATUS.DRAFT].includes(status.toUpperCase())
	);
};

export const isRFQAwarded = (status) => {
	return (
		typeof status === "string" &&
		[RFX_STATUS.AWARDED].includes(status.toUpperCase())
	);
};

export const isRFQReadyForAward = (status) => {
	return (
		typeof status === "string" &&
		[RFX_STATUS.EVALUATION].includes(status.toUpperCase())
	);
};

export const isRFQSubmissionDateExtendable = (status) => {
	return (
		typeof status === "string" &&
		[RFX_STATUS.ACTIVE].includes(status.toUpperCase())
	);
};

export const basePathAuth = (companyId) => `/account/${companyId}/`;

export const sortArrayByName = (data) => {
	let result = data.sort((a, b) => {
		let nameA = a.name.toLowerCase();
		let nameB = b.name.toLowerCase();
		if (nameA < nameB) {
			return -1;
		}
		if (nameA > nameB) {
			return 1;
		}
		return 0;
	});
	return result;
};

export const isDateWithin30Days = (dateToCheck) => {
	let afterThirtyDays = dayjs().add(30, "days");
	let today = dayjs();
	return (
		dayjs(dateToCheck).isSameOrBefore(afterThirtyDays) &&
		dayjs(dateToCheck).isSameOrAfter(today)
	);
};

export const isDateExpired = (dateToCheck) => {
	if (!(dateToCheck instanceof Date)) {
		dateToCheck = new Date(dateToCheck);
	}
	const today = new Date();
	return dateToCheck < today;
};

export const isPAPLUsers = (user) => {
	const PAPL_EMAIL = "@perthairport.com.au";
	return user?.email?.includes(PAPL_EMAIL);
};

// Keep track of recent toast IDs to prevent duplicates
const recentToastIds = new Map();

export const clearAllToastMessages = () => {
	toast.dismiss();
	recentToastIds.clear();
}

export const showToast = (subtitle, title, success, toastId) => {
	// If a toastId is provided, check if we've shown this toast recently
	if (toastId) {
		const now = Date.now();
		const lastShown = recentToastIds.get(toastId);

		// If the same toast was shown in the last 3 seconds, don't show it again
		if (lastShown && now - lastShown < 3000) {
			return;
		}

		// Update the last shown timestamp for this toastId
		recentToastIds.set(toastId, now);

		// Clean up old entries every 10 seconds to prevent memory leaks
		if (recentToastIds.size > 20) {
			const oldEntries = [...recentToastIds.entries()].filter(
				([_, timestamp]) => now - timestamp > 10000
			);
			oldEntries.forEach(([id, _]) => recentToastIds.delete(id));
		}
	}

	toast(
		<CustomToastV2
			subtitle={subtitle}
			title={title}
			success={success}
		/>,
		{
			toastId: toastId || undefined, // Use the provided toastId if available
			autoClose: success ? 3000 : 10000,
			pauseOnHover: !success,
			position: "bottom-right",
		}
	);
};

export const removePrefixFromKeys = (obj, prefix) => {
	return Object.fromEntries(
		Object.entries(obj).map(([key, value]) => [
			key.startsWith(prefix) ? key.slice(prefix.length) : key,
			value,
		])
	);
};

export const stringToBoolean = (str) => {
	return str?.toLowerCase() === "true";
};

export const formatCurrency = (value, options) => {
	if (value === null || value === undefined) {
		return "";
	}

	const numValue =
		typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;

	if (isNaN(numValue)) {
		return "";
	}

	return new Intl.NumberFormat("en-AU", {
		style: "currency",
		currency: "AUD",
		maximumFractionDigits: 2,
		...options,
	}).format(numValue);
};

export const statusNameFormatter = (status) => {
	let statusName;
	if (typeof status === "string") {
		statusName = status?.toUpperCase();
	} else {
		statusName = status;
	}
	return statusName;
};

export const isRFQDraft = (status) => {
	const statusName = statusNameFormatter(status);
	return statusName === RFX_STATUS.DRAFT;
};

/**
 * Extracts a file ID from various file object structures
 * @param {Object} fileObj - File object that may contain an ID in different formats
 * @returns {string|number|null} - The extracted ID or null if not found
 * @deprecated Use extractResumeFileId from utils/resumeHelpers.js instead
 */
export const extractFileId = extractResumeFileId;

// extract original file name from AWS S3 file name
export const getOriginalFileName = (fileName) => {
	if (!fileName) {
		return "";
	}
	// AWS S3 file name format: 40-character hash_original_file_name
	const match = fileName.match(/^[a-f0-9]{40}_(.+)$/i);
	return decodeURIComponent(match ? match[1] : fileName);
};

// Helper function to format phone number for tel: links
export const formatPhoneForTel = (phoneNumber) => {
	if (!phoneNumber) return "";

	const cleanNumber = phoneNumber.toString().replace(/\s+/g, "");

	// If it already starts with +, use as is
	if (cleanNumber.startsWith("+")) {
		return cleanNumber;
	}

	// If it starts with 0, it's Australian national format, convert to international
	if (cleanNumber.startsWith("0")) {
		return `+61${cleanNumber.substring(1)}`;
	}

	// If it starts with common country codes, add + prefix
	if (cleanNumber.match(/^(61|62|64|65|66|81|82|84|86|91|44|1)/)) {
		return `+${cleanNumber}`;
	}

	// If it's 10-11 digits and doesn't start with country code, assume Australian
	if (
		cleanNumber.length >= 10 &&
		cleanNumber.length <= 11 &&
		/^\d+$/.test(cleanNumber)
	) {
		return `+61${cleanNumber}`;
	}

	// Default: if we can't determine format, return empty string to avoid broken tel: links
	console.warn(`Unable to format phone number: ${phoneNumber}`);
	return "";
};
